import { z } from 'zod';
import type {
  InventorySort,
  InventoryVisibility,
  MerchantProductsQueryParams,
} from './service/types';

const INVENTORY_SORT_OPTIONS: InventorySort[] = ['qty', 'revenue', 'last_sold', 'name'];
const INVENTORY_VISIBILITY_OPTIONS: InventoryVisibility[] = ['visible', 'hidden', 'all'];
const INVENTORY_PAGE_SIZE_OPTIONS = [10, 20, 50] as const;
const DEFAULT_INVENTORY_PAGE_SIZE = 20;
const DEFAULT_INVENTORY_SORT: InventorySort = 'revenue';
const DEFAULT_INVENTORY_VISIBILITY: InventoryVisibility = 'visible';
const DEFAULT_INVENTORY_MISSING_IMAGE = false;

export const inventorySearchSchema = z.object({
  q: z.string().optional(),
  category: z.string().optional(),
  visibility: z.string().optional(),
  sort: z.string().optional(),
  missing_image: z.union([z.string(), z.boolean()]).optional(),
  page: z.union([z.string(), z.number()]).optional(),
  page_size: z.union([z.string(), z.number()]).optional(),
});

export type InventorySearchInput = z.infer<typeof inventorySearchSchema>;

export interface InventoryRouteSearch {
  q?: string;
  category?: string;
  visibility?: string;
  sort?: string;
  missing_image?: string | boolean;
  page?: string | number;
  page_size?: string | number;
}

export interface InventoryPageFilters {
  q?: string;
  category?: string;
  visibility?: InventoryVisibility;
  sort?: InventorySort;
  missingImage?: boolean;
  page?: number;
  pageSize?: (typeof INVENTORY_PAGE_SIZE_OPTIONS)[number];
}

function normalizePage(value: InventorySearchInput['page']): number | undefined {
  if (typeof value === 'number') {
    return Number.isInteger(value) && value >= 1 ? value : undefined;
  }
  if (typeof value !== 'string') return undefined;

  const trimmed = value.trim();
  if (!trimmed) return undefined;

  const parsed = Number(trimmed);
  if (!Number.isInteger(parsed) || parsed < 1) return undefined;

  return parsed;
}

function normalizePageSize(
  value: InventorySearchInput['page_size'],
): (typeof INVENTORY_PAGE_SIZE_OPTIONS)[number] | undefined {
  const parsed =
    typeof value === 'number'
      ? value
      : typeof value === 'string'
        ? Number(value.trim())
        : undefined;

  if (!parsed || !Number.isInteger(parsed)) return undefined;

  if (
    !INVENTORY_PAGE_SIZE_OPTIONS.includes(parsed as (typeof INVENTORY_PAGE_SIZE_OPTIONS)[number])
  ) {
    return undefined;
  }

  return parsed as (typeof INVENTORY_PAGE_SIZE_OPTIONS)[number];
}

function normalizeSort(value: InventorySearchInput['sort']): InventorySort | undefined {
  if (typeof value !== 'string') return undefined;

  const normalized = value.trim().toLowerCase();
  if (!normalized) return undefined;

  if (!INVENTORY_SORT_OPTIONS.includes(normalized as InventorySort)) return undefined;

  return normalized as InventorySort;
}

function normalizeCategory(value: InventorySearchInput['category']): string | undefined {
  if (typeof value !== 'string') return undefined;

  const normalized = value.trim().slice(0, 100);
  if (!normalized) return undefined;

  return normalized;
}

function normalizeVisibility(
  value: InventorySearchInput['visibility'],
): InventoryVisibility | undefined {
  if (typeof value !== 'string') return undefined;

  const normalized = value.trim().toLowerCase();
  if (!normalized) return undefined;

  if (!INVENTORY_VISIBILITY_OPTIONS.includes(normalized as InventoryVisibility)) {
    return undefined;
  }

  return normalized as InventoryVisibility;
}

function toHiddenFilterValue(visibility: InventoryVisibility): boolean | undefined {
  if (visibility === 'all') return undefined;
  return visibility === 'hidden';
}

function normalizeMissingImage(value: InventorySearchInput['missing_image']): boolean {
  if (typeof value === 'boolean') return value;
  if (typeof value !== 'string') return false;

  const normalized = value.trim().toLowerCase();
  return normalized === '1' || normalized === 'true';
}

export function normalizeInventorySearch(search: InventorySearchInput): InventoryRouteSearch {
  const q = search.q?.trim().slice(0, 255) || undefined;
  const category = normalizeCategory(search.category);
  const visibility = normalizeVisibility(search.visibility) ?? DEFAULT_INVENTORY_VISIBILITY;
  const normalizedSort = normalizeSort(search.sort);
  const missingImage = normalizeMissingImage(search.missing_image);
  const parsedPage = normalizePage(search.page);
  const parsedPageSize = normalizePageSize(search.page_size);

  return {
    q,
    category,
    visibility: visibility !== DEFAULT_INVENTORY_VISIBILITY ? visibility : undefined,
    sort: normalizedSort && normalizedSort !== DEFAULT_INVENTORY_SORT ? normalizedSort : undefined,
    missing_image: missingImage !== DEFAULT_INVENTORY_MISSING_IMAGE ? '1' : undefined,
    page: parsedPage && parsedPage > 1 ? parsedPage : undefined,
    page_size:
      parsedPageSize && parsedPageSize !== DEFAULT_INVENTORY_PAGE_SIZE ? parsedPageSize : undefined,
  };
}

export function areInventorySearchEqual(a: InventorySearchInput, b: InventoryRouteSearch) {
  return (
    a.q === b.q &&
    a.category === b.category &&
    a.visibility === b.visibility &&
    a.sort === b.sort &&
    a.missing_image === b.missing_image &&
    a.page === b.page &&
    a.page_size === b.page_size
  );
}

export function toInventoryPageFilters(search: InventoryRouteSearch): InventoryPageFilters {
  return {
    q: search.q,
    category: normalizeCategory(search.category),
    visibility: normalizeVisibility(search.visibility) ?? DEFAULT_INVENTORY_VISIBILITY,
    sort: normalizeSort(search.sort) ?? DEFAULT_INVENTORY_SORT,
    missingImage: normalizeMissingImage(search.missing_image),
    page: normalizePage(search.page),
    pageSize: normalizePageSize(search.page_size) ?? DEFAULT_INVENTORY_PAGE_SIZE,
  };
}

export function toInventoryRouteSearch(filters: InventoryPageFilters): InventoryRouteSearch {
  return {
    q: filters.q,
    category: filters.category,
    visibility:
      filters.visibility && filters.visibility !== DEFAULT_INVENTORY_VISIBILITY
        ? filters.visibility
        : undefined,
    sort: filters.sort && filters.sort !== DEFAULT_INVENTORY_SORT ? filters.sort : undefined,
    missing_image: filters.missingImage ? '1' : undefined,
    page: filters.page && filters.page > 1 ? filters.page : undefined,
    page_size:
      filters.pageSize && filters.pageSize !== DEFAULT_INVENTORY_PAGE_SIZE
        ? filters.pageSize
        : undefined,
  };
}

export function inventoryLoaderDeps(search: InventorySearchInput): MerchantProductsQueryParams {
  const normalized = normalizeInventorySearch(search);
  const visibility = normalizeVisibility(normalized.visibility) ?? DEFAULT_INVENTORY_VISIBILITY;

  return {
    q: normalized.q,
    category: normalizeCategory(normalized.category),
    is_hidden: toHiddenFilterValue(visibility),
    missing_image: normalizeMissingImage(normalized.missing_image) || undefined,
    sort: (normalized.sort as InventorySort | undefined) ?? DEFAULT_INVENTORY_SORT,
    page: normalizePage(normalized.page) ?? 1,
    page_size: normalizePageSize(normalized.page_size) ?? DEFAULT_INVENTORY_PAGE_SIZE,
  };
}

export {
  DEFAULT_INVENTORY_MISSING_IMAGE,
  DEFAULT_INVENTORY_PAGE_SIZE,
  DEFAULT_INVENTORY_SORT,
  DEFAULT_INVENTORY_VISIBILITY,
  INVENTORY_PAGE_SIZE_OPTIONS,
  INVENTORY_SORT_OPTIONS,
  INVENTORY_VISIBILITY_OPTIONS,
};
